// src/components/collections/CategoryItem.js

import React, { useState } from "react";
import Link from "next/link";
import { collection, doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../config/fireabseConfig"; // Ensure the path is correct
import Image from "next/image";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import emailjs from "@emailjs/browser";

const CategoryItem = ({ items }) => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    usagePurpose: "",
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: null,
    error: null,
  });

  const handleDownloadNow = (element) => {
    setSelectedElement(element);
    setIsDownloadModalOpen(true);
  };

  const closeModal = () => {
    setIsDownloadModalOpen(false);
    setSelectedElement(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      organization: "",
      role: "",
      usagePurpose: "",
    });
    setFormStatus({
      loading: false,
      success: null,
      error: null,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ loading: true, success: null, error: null });

    try {
      // 1. Save to Firestore
      const docRef = doc(collection(db, "downloadRequests"));
      const id = docRef.id;

      const downloadRequest = {
        id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        role: formData.role,
        usagePurpose: formData.usagePurpose,
        createdAt: Timestamp.now(),
      };

      await setDoc(docRef, downloadRequest);

      // 2. Send Email via EmailJS
      const emailParams = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        role: formData.role,
        usagePurpose: formData.usagePurpose,
        title: selectedElement.title, // To include which item is being downloaded
      };

      await emailjs.send(
        "service_td5jarc", // Replace with your EmailJS Service ID
        "template_cl5regq", // Replace with your EmailJS Template ID
        emailParams,
        "wOmWm38llC8kKk6bQ" // Replace with your EmailJS Public Key
      );

      setFormStatus({
        loading: false,
        success: "Your download request has been submitted successfully!",
        error: null,
      });

      // Optionally, close the modal after a delay
      setTimeout(() => {
        closeModal();
      }, 3000);
    } catch (error) {
      console.error("Error submitting download request:", error);
      setFormStatus({
        loading: false,
        success: null,
        error:
          "Failed to submit your download request. Please try again later.",
      });
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-[1.875rem] md:grid-cols-2 lg:grid-cols-4">
        {items.map((element) => {
          const { id, title, description, hbimArticleLink, imageUrl } = element;

          return (
            <article key={id}>
              <div className="dark:bg-jacarta-700 min-w-[250px] dark:border-jacarta-700 border-jacarta-100 rounded-2.5xl block border bg-white p-6 transition-shadow hover:shadow-xl">
                <figure className="relative">
                  <Link href={hbimArticleLink || `/element/${id}`}>
                    <a>
                      <Image
                        src={imageUrl}
                        alt={title}
                        width={300} // Adjust as needed
                        height={400} // Adjust as needed
                        className="w-full h-60 rounded-lg object-cover"
                      />
                    </a>
                  </Link>
                </figure>
                <div className="mt-6">
                  <Link href={hbimArticleLink || `/element/${id}`}>
                    <a>
                      <h2 className="font-display text-jacarta-700 hover:text-green text-lg dark:text-white">
                        {title}
                      </h2>
                    </a>
                  </Link>
                  <p className="text-sm text-jacarta-500 dark:text-jacarta-200 mt-3">
                    {description}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <button
                    className="text-green font-display text-sm font-semibold hover:underline"
                    onClick={() => handleDownloadNow(element)}
                  >
                    Download Now
                  </button>
                  <Link href={`/archive_post/${hbimArticleLink}`}>
                    <a className="group flex items-center space-x-1">
                      <svg
                        className="icon icon-history group-hover:fill-green dark:fill-jacarta-200 fill-jacarta-500 h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10c5.523 0 10-4.477 10-10S17.523 2 12 2zm1 17.93a.996.996 0 01-1.414 0l-4.243-4.242a.996.996 0 011.414-1.414L12 16.586l3.829-3.829a.996.996 0 111.414 1.414l-4.243 4.242z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="group-hover:text-green font-display dark:text-jacarta-200 text-sm font-semibold">
                        View Article
                      </span>
                    </a>
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Download Request Modal */}
      {isDownloadModalOpen && selectedElement && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white dark:bg-jacarta-700 rounded-lg p-6 w-11/12 max-w-md relative overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <h2 className="text-2xl font-semibold mb-4 text-jacarta-700 dark:text-white">
              Download {selectedElement.title}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* First Name */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  First Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                  placeholder="John"
                />
              </div>

              {/* Last Name */}
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Last Name<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                  placeholder="Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email<span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                  placeholder="john.doe@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Phone Number<span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              {/* Organization */}
              <div>
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Organization<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  required
                  value={formData.organization}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                  placeholder="Your Organization"
                />
              </div>

              {/* Role */}
              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Role<span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-white shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                >
                  <option value="">Select your role</option>
                  <option value="Student">Student</option>
                  <option value="Employee">Employee</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Usage Purpose */}
              <div>
                <label
                  htmlFor="usagePurpose"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  What is the purpose of your download?
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="usagePurpose"
                  name="usagePurpose"
                  required
                  value={formData.usagePurpose}
                  onChange={handleChange}
                  rows="4"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 dark:bg-jacarta-700 dark:border-jacarta-600 dark:text-white"
                  placeholder="Describe how you intend to use the model..."
                ></textarea>
              </div>

              {/* Submission Status */}
              {formStatus.success && (
                <div className="text-green-600 text-sm text-center">
                  {formStatus.success}
                </div>
              )}
              {formStatus.error && (
                <div className="text-red-600 text-sm text-center">
                  {formStatus.error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-jacarta-600 dark:text-gray-300 dark:hover:bg-jacarta-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formStatus.loading}
                  className={`px-4 py-2 text-white bg-lime-900 rounded hover:bg-green-700 focus:outline-none ${
                    formStatus.loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {formStatus.loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
            {/* Confirmation Message */}
            {formStatus.success && (
              <p className="mt-4 text-center text-green-600">
                Dear User, thank you for your download request! We will process
                it and get back to you shortly.
              </p>
            )}
          </div>
        </div>
      )}
      {/* End Download Request Modal */}
    </>
  );
};

export default CategoryItem;
